/**
 * QuoteTerms.jsx — Per-quote terms editor (two-column)
 *
 * Left column: sortable list of all terms (library + custom) with drag handles,
 * on/off toggles, status badges, and an "+ Add Custom Clause" button.
 * Right column: detail / edit pane for the selected term.
 *
 * Library terms can be edited "for this quote only" (per-quote override) or
 * reset to the library default. Custom (ad-hoc) terms have label + body
 * edited freely and can be deleted. All edits are dispatched into useQuoteBuilder
 * and snapshot-saved with the quote.
 */

import { useMemo, useState, useEffect, useRef } from 'react';
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import SaveTermToLibraryDialog from './SaveTermToLibraryDialog';
import { useFocus } from '../../hooks/useFocus';
import FocusHide from '../shared/FocusHide';

const TYPE_LABELS = {
  core: 'Core',
  conditional: 'Conditional',
  optional: 'Optional',
  custom: 'Custom',
};

function CategoriesLabel({ categories }) {
  if (!Array.isArray(categories) || categories.length === 0) return null;
  return (
    <span className="text-2xs text-text-dim">{categories.join(', ')}</span>
  );
}

function StatusBadges({ term, isAutoConditional }) {
  return (
    <div className="flex items-center gap-1 flex-shrink-0">
      {term.source === 'custom' && (
        <span className="text-2xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30">
          Custom
        </span>
      )}
      {term.isOverridden && (
        <span className="text-2xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
          Edited
        </span>
      )}
      {isAutoConditional && (
        <span className="text-2xs px-1.5 py-0.5 rounded bg-surface-2 text-text-dim border border-border">
          Auto
        </span>
      )}
    </div>
  );
}

function SortableTermRow({
  term,
  isSelected,
  isAutoConditional,
  onSelect,
  onToggle,
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: term.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-2 px-2 py-2 rounded border transition-colors ${
        isSelected
          ? 'border-accent/60 bg-accent/5'
          : term.on
            ? 'border-border bg-surface'
            : 'border-border/50 bg-surface/40'
      }`}
    >
      {/* Drag handle */}
      <button
        {...attributes}
        {...listeners}
        data-track="quote.term.reorder"
        className="cursor-grab active:cursor-grabbing text-text-dim hover:text-text-muted text-sm px-1 select-none"
        aria-label={`Reorder ${term.label}`}
        title="Drag to reorder"
      >
        ⋮⋮
      </button>

      {/* Toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggle(term.id); }}
        data-track="quote.term.toggle"
        className={`w-8 h-5 rounded-full relative flex-shrink-0 transition-colors ${
          term.on ? 'bg-accent' : 'bg-surface-3 border border-border'
        }`}
        aria-label={term.on ? `Disable ${term.label}` : `Enable ${term.label}`}
      >
        <span
          className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
            term.on ? 'left-3.5' : 'left-0.5'
          }`}
        />
      </button>

      {/* Label + internal note */}
      <button
        onClick={() => onSelect(term.id)}
        data-track="quote.term.select"
        className="flex-1 min-w-0 text-left flex items-baseline gap-1.5"
        title={term.note ? `${term.label} — ${term.note}` : term.label}
      >
        <span
          className={`text-sm whitespace-nowrap ${
            term.on ? 'text-text-primary' : 'text-text-dim'
          }`}
        >
          {term.label || <em className="text-text-dim">Untitled</em>}
        </span>
        {term.note && (
          <span className="text-2xs text-text-dim italic truncate min-w-0">
            · {term.note}
          </span>
        )}
      </button>

      <StatusBadges term={term} isAutoConditional={isAutoConditional} />
    </div>
  );
}

export default function QuoteTerms({
  activeTerms = [],
  selectedCategoryIds = [],
  libraryLabels = [],
  onToggleTerm,
  onEditTermBody,
  onResetTerm,
  onAddCustom,
  onUpdateCustom,
  onRemoveCustom,
  onPromoteToLibrary,
  onReorder,
}) {
  const [selectedId, setSelectedId] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  // Focus hides Edit on library terms only — a custom clause has no other way
  // to be edited once '+ Add Custom Clause' has closed its editor.
  const { isHidden } = useFocus();
  const termEditHidden = isHidden('quote.termEdit');
  // Track custom-term IDs we've already seen so we can auto-select brand new ones.
  const prevCustomIdsRef = useRef(null); // null = uninitialised (first run will seed)

  const catSet = useMemo(() => new Set(selectedCategoryIds), [selectedCategoryIds]);
  const ids = useMemo(() => activeTerms.map((t) => t.id), [activeTerms]);

  const selected = useMemo(
    () => activeTerms.find((t) => t.id === selectedId) || null,
    [activeTerms, selectedId]
  );

  // Reset edit mode when selection changes (no draft to flush — inputs are
  // live-bound to the reducer, so every keystroke is already committed).
  useEffect(() => {
    setEditMode(false);
  }, [selectedId]);

  // Auto-select + auto-edit only BRAND NEW custom terms (added via
  // ADD_CUSTOM_TERM during this session). Pre-existing custom terms loaded
  // from a saved quote must NOT auto-open in edit mode — they should just
  // render in view mode like any other term.
  useEffect(() => {
    const currentCustomIds = new Set(
      activeTerms.filter((t) => t.source === 'custom').map((t) => t.id)
    );
    if (prevCustomIdsRef.current === null) {
      // First run after mount: just seed the ref. Existing custom terms are
      // not considered "new" here so we don't pop the editor open.
      prevCustomIdsRef.current = currentCustomIds;
      return;
    }
    const prev = prevCustomIdsRef.current;
    const newIds = [...currentCustomIds].filter((id) => !prev.has(id));
    if (newIds.length === 1) {
      setSelectedId(newIds[0]);
      setEditMode(true);
    }
    prevCustomIdsRef.current = currentCustomIds;
  }, [activeTerms]);

  // ── Live-bind handlers — every keystroke dispatches immediately so saving
  //    the quote always uses the latest text. No draft state, no race with
  //    blur/click ordering. ──
  const handleLabelChange = (e) => {
    if (!selected) return;
    const value = e.target.value;
    if (selected.source === 'custom') {
      onUpdateCustom?.(selected.id, { label: value });
    } else {
      onEditTermBody?.(selected.id, { label: value });
    }
  };

  const handleBodyChange = (e) => {
    if (!selected) return;
    const value = e.target.value;
    if (selected.source === 'custom') {
      onUpdateCustom?.(selected.id, { body: value });
    } else {
      onEditTermBody?.(selected.id, { body: value });
    }
  };

  const handleNoteChange = (e) => {
    if (!selected) return;
    const value = e.target.value;
    if (selected.source === 'custom') {
      onUpdateCustom?.(selected.id, { note: value });
    } else {
      onEditTermBody?.(selected.id, { note: value });
    }
  };

  // ── Drag-and-drop sensors ──
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = ids.indexOf(active.id);
    const newIndex = ids.indexOf(over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    onReorder?.(arrayMove(ids, oldIndex, newIndex));
  };

  // ── Button handlers ──
  const beginEdit = () => setEditMode(true);
  const doneEditing = () => setEditMode(false);

  const resetToDefault = () => {
    if (!selected || selected.source === 'custom') return;
    onResetTerm?.(selected.id);
    setEditMode(false);
  };

  const removeCustom = () => {
    if (!selected || selected.source !== 'custom') return;
    if (!confirm(`Delete custom clause "${selected.label || 'Untitled'}"?`)) return;
    onRemoveCustom?.(selected.id);
    setSelectedId(null);
  };

  const openSaveDialog = () => {
    if (!selected || selected.source !== 'custom') return;
    if (!String(selected.body || '').trim()) {
      alert('Add a body to this clause before saving it to the library.');
      return;
    }
    setSaveDialogOpen(true);
  };

  const handleConfirmSave = async (form) => {
    if (!selected || selected.source !== 'custom') return;
    try {
      await onPromoteToLibrary?.({
        customId: selected.id,
        label: form.label,
        body: selected.body || '',
        type: form.type,
        triggerCategories: form.triggerCategories,
        enableByDefault: form.enableByDefault,
      });
      setSaveDialogOpen(false);
      setSelectedId(null);
      setEditMode(false);
    } catch (err) {
      // Parent surfaces the error toast; keep dialog open so user can retry.
      // eslint-disable-next-line no-console
      console.error('Save to library failed', err);
    }
  };

  const addCustomClause = () => {
    onAddCustom?.({ label: 'New clause', body: '' });
    // Auto-select handled by the effect above watching for new custom IDs.
  };

  const enabledCount = activeTerms.filter((t) => t.on).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-heading text-text-primary uppercase tracking-wide">
          Quote Terms
        </h3>
        <span className="text-2xs text-text-dim">
          {enabledCount} of {activeTerms.length} active
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)] gap-3">
        {/* ── Left: sortable list ── */}
        <div className="space-y-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={ids} strategy={verticalListSortingStrategy}>
              <div className="space-y-1 max-h-[440px] overflow-y-auto pr-1">
                {activeTerms.length === 0 ? (
                  <div className="text-xs text-text-dim italic p-3 text-center border border-dashed border-border rounded">
                    No terms yet.
                  </div>
                ) : (
                  activeTerms.map((term) => {
                    const isAutoConditional =
                      term.type === 'conditional' &&
                      Array.isArray(term.triggerCategories) &&
                      term.triggerCategories.length > 0 &&
                      term.triggerCategories.some((c) => catSet.has(c));
                    return (
                      <SortableTermRow
                        key={term.id}
                        term={term}
                        isSelected={term.id === selectedId}
                        isAutoConditional={isAutoConditional}
                        onSelect={setSelectedId}
                        onToggle={onToggleTerm}
                      />
                    );
                  })
                )}
              </div>
            </SortableContext>
          </DndContext>

          <button
            onClick={addCustomClause}
            className="w-full px-3 py-2 text-xs font-medium bg-surface border border-dashed border-border text-text-muted rounded hover:bg-surface-2 hover:border-accent/40 hover:text-text-primary transition-colors"
          >
            + Add Custom Clause
          </button>
        </div>

        {/* ── Right: detail / edit pane ── */}
        <div className="border border-border rounded-lg bg-surface min-h-[220px] p-3">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-xs text-text-dim italic text-center px-4">
              Select a term on the left to view or edit it.
            </div>
          ) : editMode ? (
            <div className="space-y-3">
              <div>
                <label className="text-2xs text-text-dim uppercase mb-1 block">
                  Label
                </label>
                <input
                  type="text"
                  value={selected.label || ''}
                  onChange={handleLabelChange}
                  className="field w-full text-sm"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-2xs text-text-dim uppercase mb-1 block">
                  Body {selected.source !== 'custom' && '(this quote only)'}
                </label>
                <textarea
                  value={selected.body || ''}
                  onChange={handleBodyChange}
                  rows={8}
                  className="field w-full text-xs leading-relaxed resize-y"
                />
              </div>
              <div>
                <label className="text-2xs text-text-dim uppercase mb-1 block">
                  Internal note
                </label>
                <textarea
                  value={selected.note || ''}
                  onChange={handleNoteChange}
                  rows={2}
                  placeholder="Reminder for you — not shown to the client"
                  className="field w-full text-xs leading-relaxed resize-y"
                />
                <p className="text-2xs text-text-dim italic mt-1">
                  Visible only in the portal. Never on the quote PDF or email.
                </p>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <button onClick={doneEditing} className="btn-primary px-3 py-1.5 text-xs">
                  Done
                </button>
                {selected.source !== 'custom' && selected.isOverridden && (
                  <FocusHide k="quote.termEdit">
                  <button
                    onClick={resetToDefault}
                    className="px-3 py-1.5 text-xs bg-surface-2 border border-border text-text-muted rounded hover:bg-surface-3 transition-colors"
                  >
                    Reset to library default
                  </button>
                  </FocusHide>
                )}
                {selected.source === 'custom' && onPromoteToLibrary && (
                  <FocusHide k="quote.termEdit">
                  <button
                    onClick={openSaveDialog}
                    className="px-3 py-1.5 text-xs bg-surface-2 border border-border text-text-muted rounded hover:bg-surface-3 hover:text-text-primary transition-colors"
                    title="Save this clause to your library so it appears on future quotes"
                  >
                    Save to library
                  </button>
                  </FocusHide>
                )}
                {selected.source === 'custom' && (
                  <button
                    onClick={removeCustom}
                    className="ml-auto px-3 py-1.5 text-xs bg-red-500/10 border border-red-500/30 text-red-400 rounded hover:bg-red-500/20 transition-colors"
                  >
                    Delete clause
                  </button>
                )}
              </div>
              <p className="text-2xs text-text-dim italic">
                Changes save automatically as you type. Click Done when finished.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <h4 className="text-sm font-semibold text-text-primary truncate">
                    {selected.label || <em className="text-text-dim">Untitled</em>}
                  </h4>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    {selected.type && (
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-surface-2 text-text-dim">
                        {TYPE_LABELS[selected.type] || selected.type}
                      </span>
                    )}
                    {selected.source === 'custom' && (
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-blue-500/10 text-blue-400 border border-blue-500/30">
                        Custom clause
                      </span>
                    )}
                    {selected.isOverridden && (
                      <span className="text-2xs px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/30">
                        Edited for this quote
                      </span>
                    )}
                    <CategoriesLabel categories={selected.triggerCategories} />
                  </div>
                </div>
                {(selected.source === 'custom' || !termEditHidden) && (
                <div className="flex gap-1 flex-shrink-0">
                  <button
                    data-focus={selected.source !== 'custom' ? 'quote.termEdit' : undefined}
                    onClick={beginEdit}
                    className="px-2.5 py-1 text-2xs bg-surface-2 border border-border text-text-muted rounded hover:bg-surface-3 hover:text-text-primary transition-colors"
                  >
                    Edit
                  </button>
                  {selected.source === 'custom' && onPromoteToLibrary && (
                    <FocusHide k="quote.termEdit">
                    <button
                      onClick={openSaveDialog}
                      className="px-2.5 py-1 text-2xs bg-surface-2 border border-border text-text-muted rounded hover:bg-surface-3 hover:text-text-primary transition-colors"
                      title="Save this clause to your library so it appears on future quotes"
                    >
                      Save to library
                    </button>
                    </FocusHide>
                  )}
                  {selected.source === 'custom' && (
                    <button
                      onClick={removeCustom}
                      className="px-2.5 py-1 text-2xs bg-red-500/10 border border-red-500/30 text-red-400 rounded hover:bg-red-500/20 transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </div>
                )}
              </div>
              <div className="text-xs text-text-dim leading-relaxed whitespace-pre-wrap border-t border-border/50 pt-3">
                {selected.body || <em>No body. Click Edit to add one.</em>}
              </div>
              {selected.note && (
                <div className="rounded border border-amber-500/20 bg-amber-500/5 px-2.5 py-2">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="text-2xs font-medium text-amber-400 uppercase tracking-wider">
                      Internal note
                    </span>
                    <span className="text-2xs text-text-dim">· not shown to client</span>
                  </div>
                  <div className="text-2xs text-text-muted leading-relaxed whitespace-pre-wrap">
                    {selected.note}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <SaveTermToLibraryDialog
        open={saveDialogOpen && !!selected && selected.source === 'custom'}
        initialLabel={selected?.label || ''}
        initialBody={selected?.body || ''}
        suggestedCategories={selectedCategoryIds}
        existingLabels={libraryLabels}
        onCancel={() => setSaveDialogOpen(false)}
        onSave={handleConfirmSave}
      />
    </div>
  );
}
